import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { authenticateWithTelegram, onPhoneRequired } from '@/api/telegramAuth';

interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface TelegramContextType {
  user: {
    id: number;
    firstName: string;
    lastName?: string;
    username?: string;
  } | null;
  isReady: boolean;
  /** initData, по которой выполнен вход (для POST /auth/telegram/link). */
  initData: string | null;
  /**
   * Вход «сначала номер»: карточки с этим Telegram ID ещё нет, сервер ждёт номер (или отказ от него)
   * — до основного приложения показывается экран номера (PhoneFirstGate). Сессии пока нет.
   */
  phoneRequired: boolean;
  /** Экран номера пройден: сессия получена через /auth/telegram/link. */
  completePhoneStep: () => void;
  hapticFeedback: (style: 'light' | 'medium' | 'heavy') => void;
  safeAreaInset: SafeAreaInset;
  contentSafeAreaInset: SafeAreaInset;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

const DEFAULT_SAFE_AREA: SafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };

const SDK_WAIT_TIMEOUT_MS = 3000;
const SDK_POLL_INTERVAL_MS = 100;

/**
 * Ждёт появления Telegram WebApp SDK. Скрипт грузится с внешнего CDN (telegram.org), и на
 * медленной сети или через VPN он может приехать позже первого рендера, а то и не приехать
 * вовсе. Одноразовая проверка на маунте в таком случае навсегда оставляла клиента «гостем»
 * с балансом 0 — при живых баллах в базе (инцидент MELMAN, август 2026).
 */
type TelegramWebApp = NonNullable<typeof window.Telegram>['WebApp'];

async function waitForTelegramWebApp(): Promise<TelegramWebApp | null> {
  const deadline = Date.now() + SDK_WAIT_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (window.Telegram?.WebApp) {
      return window.Telegram.WebApp;
    }
    await new Promise((resolve) => setTimeout(resolve, SDK_POLL_INTERVAL_MS));
  }
  return window.Telegram?.WebApp ?? null;
}

/**
 * Updates CSS custom properties with safe area values
 */
function updateSafeAreaCSS(safeArea: SafeAreaInset, contentSafeArea: SafeAreaInset) {
  const root = document.documentElement;
  root.style.setProperty('--tg-safe-area-top', `${safeArea.top}px`);
  root.style.setProperty('--tg-safe-area-bottom', `${safeArea.bottom}px`);
  root.style.setProperty('--tg-content-safe-area-top', `${contentSafeArea.top}px`);
  root.style.setProperty('--tg-content-safe-area-bottom', `${contentSafeArea.bottom}px`);
}

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState<TelegramContextType['user']>(null);
  const [initData, setInitData] = useState<string | null>(null);
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [safeAreaInset, setSafeAreaInset] = useState<SafeAreaInset>(DEFAULT_SAFE_AREA);
  const [contentSafeAreaInset, setContentSafeAreaInset] = useState<SafeAreaInset>(DEFAULT_SAFE_AREA);

  // Повторный вход (401 от API) узнал, что карточки нет, — например, первый вход сорвался по сети.
  useEffect(() => onPhoneRequired(() => setPhoneRequired(true)), []);

  const completePhoneStep = useCallback(() => setPhoneRequired(false), []);

  useEffect(() => {
    // Вход «сначала номер»: новый клиент получает phone_required и экран номера; карточка есть —
    // сессия сразу. Старый бэкенд phoneFirst не знает и отдаёт сессию как раньше.
    const signIn = async (data: string) => {
      setInitData(data);
      const status = await authenticateWithTelegram(data, { phoneFirst: true });
      if (status === 'phone_required') {
        setPhoneRequired(true);
      }
    };

    (async () => {
    try {
      // Initialize Telegram Mini App
      const webApp = await waitForTelegramWebApp();
      if (webApp) {
        const tg = webApp;

        // Configure and expand the app
        tg.ready();
        tg.expand();

        // Disable vertical swipes to close (if available)
        if ('enableClosingConfirmation' in tg) {
          (tg as any).enableClosingConfirmation();
        }

        // Set header color to match app background (if available)
        if ('setHeaderColor' in tg) {
          (tg as any).setHeaderColor('bg_color');
        }

        // Lock orientation if needed (if available)
        if ('isVersionAtLeast' in tg && (tg as any).isVersionAtLeast('6.1')) {
          if ('lockOrientation' in tg) {
            (tg as any).lockOrientation();
          }
        }

        // Request fullscreen mode if available (Telegram v8.0+)
        if ('requestFullscreen' in tg) {
          try {
            (tg as any).requestFullscreen();
          } catch {
            // Fullscreen not supported by this Telegram version
          }
        }

        // Get safe area insets from Telegram WebApp
        const getSafeAreaInsets = () => {
          const tgAny = tg as any;

          // safeAreaInset - device safe area (notch, home indicator)
          const safeArea: SafeAreaInset = tgAny.safeAreaInset || DEFAULT_SAFE_AREA;

          // contentSafeArea - safe area for content (accounts for Telegram UI)
          const contentSafeArea: SafeAreaInset = tgAny.contentSafeArea || DEFAULT_SAFE_AREA;

          setSafeAreaInset(safeArea);
          setContentSafeAreaInset(contentSafeArea);
          updateSafeAreaCSS(safeArea, contentSafeArea);
        };

        // Get initial safe area values
        getSafeAreaInsets();

        // Listen for safe area changes (viewport resize, fullscreen toggle)
        if ('onEvent' in tg) {
          (tg as any).onEvent('safeAreaChanged', getSafeAreaInsets);
          (tg as any).onEvent('contentSafeAreaChanged', getSafeAreaInsets);
          (tg as any).onEvent('viewportChanged', getSafeAreaInsets);
        }

        // Get user data from Telegram
        if (tg.initDataUnsafe?.user) {
          const tgUser = tg.initDataUnsafe.user;
          setUser({
            id: tgUser.id,
            firstName: tgUser.first_name,
            lastName: tgUser.last_name,
            username: tgUser.username,
          });

          // Verify initData server-side and obtain a session JWT
          if (tg.initData) {
            await signIn(tg.initData);
          }
        } else if (import.meta.env.DEV) {
          // Development-only fallback: test user without real Telegram session
          const devInitData = 'user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Test%22%7D&auth_date=0&hash=dev';
          setUser({ id: 123456789, firstName: 'Test User', username: 'testuser' });
          await signIn(devInitData);
        }

        setIsReady(true);
      } else if (import.meta.env.DEV) {
        // Development in browser — no Telegram WebApp available
        const devInitData = 'user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Test%22%7D&auth_date=0&hash=dev';
        setUser({ id: 123456789, firstName: 'Test User', username: 'testuser' });
        await signIn(devInitData);
        setIsReady(true);
      } else {
        // Production without Telegram WebApp — should not happen
        setIsReady(true);
      }
    } catch (error) {
      console.error('Failed to initialize Telegram Mini App:', error);
      if (import.meta.env.DEV) {
        setUser({ id: 123456789, firstName: 'Test User', username: 'testuser' });
      }
      setIsReady(true);
    }
    })();
  }, []);

  const hapticFeedback = (style: 'light' | 'medium' | 'heavy') => {
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred(style);
      }
    } catch (error) {
      // Haptic not supported
    }
  };

  return (
    <TelegramContext.Provider
      value={{
        user,
        isReady,
        initData,
        phoneRequired,
        completePhoneStep,
        hapticFeedback,
        safeAreaInset,
        contentSafeAreaInset,
      }}
    >
      {children}
    </TelegramContext.Provider>
  );
}

export function useTelegram() {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within TelegramProvider');
  }
  return context;
}


